import React, { useState } from "react";
import { Loader } from "lucide-react";
import { useConversionLog } from "../hooks/useTreasury";
import ConversionLogFilters from "./ConversionLogFilters";

const formatAmount = (value) => {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  if (isNaN(num)) return "—";
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
};

const formatRate = (value) => (value === null || value === undefined ? "N/A" : formatAmount(value));

// Merchant-only (see the treasury implementation plan's own answer on why
// a consumer equivalent isn't structurally possible) - one row per
// BusinessFxTrade that a payout actually settled and captured a partner
// rate for. Deliberately does NOT show a P&L column - the backend doesn't
// compute one yet (novacrustExchangeRate and partnerSettlementRate use
// rate-direction conventions that haven't been cross-confirmed against
// real data - see ConversionLogService's own comment). Both rates are
// shown side by side so finance can eyeball them until that's confirmed.
function SettlementLinkedTable() {
  const [currency, setCurrency] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data, isLoading, isError, error } = useConversionLog({
    currency: currency.trim().toUpperCase(),
    from,
    to,
  });

  if (isError) {
    return (
      <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
        Error: {error.message}
      </p>
    );
  }

  const rows = data ?? [];

  return (
    <div>
      <p className="mb-4 text-sm text-gray-500">
        Conversions a payout actually settled at a partner - internal rate vs. the partner's
        reported settlement rate, shown raw. A profit/loss figure isn't computed yet - the rate
        directions haven't been confirmed against enough real rows to trust a formula.
      </p>
      <ConversionLogFilters
        currency={currency}
        onCurrencyChange={setCurrency}
        from={from}
        onFromChange={setFrom}
        to={to}
        onToChange={setTo}
      />

      {isLoading ? (
        <div className="flex h-[300px]">
          <div className="flex flex-1 justify-center items-center">
            <p className="flex gap-2 items-center font-medium text-gray-600">
              Loading conversion log <Loader className="w-5 h-5 animate-spin" />
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Date</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Business</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Currency</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Amount</th>
                  <th title="The provider's base rate, as stored, before our spread" className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Provider Rate</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Novacrust Rate</th>
                  <th title="What the spread earned, in the non-USD currency of the pair" className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Spread</th>
                  <th title="Platform fee, in the source currency" className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Platform Fee</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Provider</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Partner Settlement Rate</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Captured</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length > 0 ? (
                  rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-gray-50">
                      <td className="px-4 py-3 font-sans text-sm text-gray-600 whitespace-nowrap md:px-6 md:py-5">
                        {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-gray-900 md:px-6 md:py-5">
                        {row.business?.name ?? "—"}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-gray-600 uppercase md:px-6 md:py-5">
                        {row.sourceCurrency} → {row.destinationCurrency}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                        {formatAmount(row.sourceAmount)} → {formatAmount(row.destinationAmount)}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                        {formatRate(row.providerExchangeRate)}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                        {formatRate(row.novacrustExchangeRate)}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                        {row.transactionSpread === null || row.transactionSpread === undefined ? "N/A" : `${formatAmount(row.transactionSpread)} ${(row.spreadCurrency || "").toUpperCase()}`}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                        {row.platformFee === null || row.platformFee === undefined ? "N/A" : `${formatAmount(row.platformFee)} ${(row.platformFeeCurrency || "").toUpperCase()}`}
                        {row.earningsFromConsumerTrade && (
                          <span
                            className="block text-xs text-gray-400"
                            title="The house merchant converts at the base rate, so the spread and fee shown here are the ones on the consumer's own conversion."
                          >
                            from consumer trade
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-gray-600 capitalize md:px-6 md:py-5">
                        {row.rateProvider?.toLowerCase() ?? "N/A"}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                        {formatRate(row.partnerSettlementRate)}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-gray-600 whitespace-nowrap md:px-6 md:py-5">
                        {row.partnerSettlementRateCapturedAt
                          ? new Date(row.partnerSettlementRateCapturedAt).toLocaleString()
                          : "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={11} className="px-6 py-12 text-center text-gray-500">
                      No settled conversions match the current filters yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettlementLinkedTable;
